'use client';

import { useSyncExternalStore } from 'react';

/**
 * Is an offline surface currently on screen?
 *
 * ── WHY THIS ISN'T JUST A PATHNAME CHECK ────────────────────────────────────
 *
 * `hidesAppChrome` already hides the Mucka Bar on `/offline`, and that is not
 * enough, because the service worker answers a FAILED navigation with the
 * offline page's HTML while the address bar keeps the URL that was asked for.
 * A cold start offline is `/` showing the offline shell; a tapped certificate
 * is `/gas-certs/<id>` showing it. Neither pathname is `/offline`, so the nav
 * bar rendered straight over the top — reported from a device as a row of tabs
 * that do nothing.
 *
 * The surface itself is the only thing that reliably knows it is on screen, so
 * it says so, and the chrome listens. A plain module-level flag rather than
 * context: `MobileNav` is mounted in the ROOT layout and the offline surfaces
 * are nowhere near it in the tree, so there is no provider that could sit above
 * both without wrapping the entire app.
 */

let open = false;
const listeners = new Set<() => void>();

/** Called by an offline surface on mount, and with `false` on unmount. */
export function setOfflineSurfaceOpen(next: boolean): void {
  if (open === next) return;
  open = next;
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function getSnapshot(): boolean {
  return open;
}

/**
 * SSR renders the chrome as normal — the offline surface only ever appears on
 * the client, so claiming otherwise on the server would flash the bar away on
 * every ordinary page load.
 */
function getServerSnapshot(): boolean {
  return false;
}

export function useOfflineSurfaceOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
